import { type ReactElement } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import LoadingButton from "@atlaskit/button/loading-button";
import Form, { type OnSubmitHandler } from "@atlaskit/form";
import SectionMessage, {
    SectionMessageAction
} from "@atlaskit/section-message";

import {
    BottomNav,
    FormFooter,
    FormHeader,
    FormSection,
    SiteNameDropdown,
    type SiteNameDropdownOption
} from "../../ui";

export type SiteNameDropdownFormProps = {
    readonly sites: SiteNameDropdownOption[];
    readonly onSubmit: OnSubmitHandler<SiteNameDropdownData>;
    readonly newSite: (value: boolean) => void;
    readonly renderPreview?: (value: string) => ReactElement;
    readonly error?: { isConflictError?: boolean };
    readonly onChange?: (value: string) => void;
    readonly provisionableProductCard?: ReactElement;
    readonly organizationCard?: ReactElement;
};

export type SiteNameDropdownData = {
    siteName: SiteNameDropdownOption;
};

export const SiteNameDropdownForm = ({
    newSite,
    onSubmit,
    renderPreview,
    sites,
    onChange,
    error,
    provisionableProductCard,
    organizationCard
}: SiteNameDropdownFormProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const searchParams = new URLSearchParams(window.location.search);
    const orderId = searchParams.get("orderId") || "";

    const postOrderConflictErrorLink = (
        <SectionMessageAction
            onClick={() => {
                createAnalyticsEvent({
                    eventName: "postOrderConflictError",
                    eventComponent: "direct-buy-site-setup",
                    event: "clicked",
                    actionSubject: "postOrderConflictErrorLink",
                    eventType: "ui",
                    attributes: {
                        orderId
                    }
                }).fire("signup");
                window.open("https://support.atlassian.com/contact/", "_blank");
            }}
        >
            Contact support
        </SectionMessageAction>
    );
    const postOrderConflictError = (
        <SectionMessage
            testId="dropdown-post-order-conflict-error-message"
            appearance="error"
            title="We’re unable to set up your app on this site"
            actions={[postOrderConflictErrorLink]}
        >
            This site already includes one or more apps from the order. Select
            another site or create a new site. Reach out to us if you need help
            with your order ID: {orderId}.
        </SectionMessage>
    );

    return (
        <Form<{ siteName: SiteNameDropdownOption }> onSubmit={onSubmit}>
            {({ formProps, submitting }) => (
                <form {...formProps} data-testid="site-name-dropdown-form">
                    <FormHeader
                        title="Name your site"
                        description="Your site name is included in the URL, so be sure the name is something your teams will recognize. Don't worry! You can always change it later."
                    />
                    {/* Display provisionable products above the site picker */}
                    {!!provisionableProductCard && provisionableProductCard}
                    {!!organizationCard && organizationCard}
                    <FormSection>
                        <SiteNameDropdown
                            fieldName="siteName"
                            fieldLabel="Select one of your existing sites"
                            renderPreview={renderPreview}
                            sites={sites}
                            onChange={onChange}
                        />
                    </FormSection>
                    {!!error?.isConflictError && (
                        <FormSection>{postOrderConflictError}</FormSection>
                    )}
                    <FormFooter>
                        <LoadingButton
                            type="submit"
                            appearance="primary"
                            shouldFitContainer
                            isLoading={submitting}
                            isDisabled={!!error?.isConflictError}
                        >
                            Choose this site
                        </LoadingButton>
                    </FormFooter>
                    <BottomNav normalText="You can also">
                        <Button
                            appearance="link"
                            spacing="none"
                            onClick={() => newSite(true)}
                        >
                            add a new site
                        </Button>
                    </BottomNav>
                </form>
            )}
        </Form>
    );
};
